/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  transactionService,
  type GetTransactionsParams,
  type TransactionLineItemRecord,
  type TransactionRecord,
  type TransactionTimelineRecord,
} from "@src/@api-services/transaction";

import { SortOrder } from "@src/shared/constants/sort-order.constants";
import type { PaginatedListResponse } from "@src/shared/types/pagination.types";
import type {
  PlatformTransaction,
  TransactionLineItem,
  TransactionTimelineEvent,
} from "@src/views/apps/financial/types";

interface TransactionsState {
  data: PlatformTransaction[];
  total: number;
  params: GetTransactionsParams;
  selectedTransaction: PlatformTransaction | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

const mapTimelineEvent = (
  event: TransactionTimelineRecord
): TransactionTimelineEvent => ({
  event: event.event || "",
  notes: event.notes ?? null,
  performedBy: event.performedBy ?? null,
  performedByType: event.performedByType ?? null,
  createdAt: event.createdAt || new Date().toISOString(),
});

const mapLineItem = (item: TransactionLineItemRecord): TransactionLineItem => {
  const productName = item.productName ?? null;

  return {
    id: String(item.id),
    name: productName ?? item.name ?? item.description ?? null,
    productName,
    description: item.description ?? undefined,
    quantity: Number(item.quantity || 0),
    price: Number(item.price ?? item.unitPrice ?? 0),
    unitPrice: Number(item.price ?? item.unitPrice ?? 0),
    taxAmount: Number(item.taxAmount ?? 0),
    discountAmount: Number(item.discountAmount ?? 0),
    taxRate: Number(item.taxRate ?? 0),
    couponDiscountAmount: Number(item.couponDiscountAmount ?? 0),
    shippingCost: Number(item.shippingCost ?? 0),
    discountId: item.discountId ?? null,
    couponId: item.couponId ?? null,
    date: item.date ?? null,
    time: item.time ?? null,
    total: Number(item.total ?? item.totalAmount ?? 0),
    itemType: item.itemType ?? undefined,
    itemId: item.itemId ?? undefined,
    businessId: item.businessId,
    businessName: item.businessName ?? undefined,
    imageUrl: item.imageUrl || null,
    category: item.category ?? null,
    timeline: Array.isArray(item.timeline)
      ? item.timeline.map(mapTimelineEvent)
      : [],
  };
};

const mapTransaction = (row: TransactionRecord): PlatformTransaction => {
  const lineItems = Array.isArray(row.lineItems)
    ? row.lineItems.map(mapLineItem)
    : [];

  const businessNames = Array.isArray(row.businessNames)
    ? row.businessNames.filter(Boolean)
    : [
        ...new Set(
          lineItems
            .map((item) => item.businessName)
            .filter((name): name is string => Boolean(name))
        ),
      ];

  return {
    id: String(row.id),
    type: row.transactionType ?? row.type ?? null,
    userName: row.userName ?? null,
    userEmail: row.userEmail ?? "",
    ...(row.businessName || businessNames.length
      ? {
          businessName: row.businessName || businessNames.join(", "),
          businessNames: businessNames.length > 0 ? businessNames : undefined,
        }
      : {}),
    paymentMethod: row.paymentMethod ?? null,
    status: row.status ?? null,
    subtotal: Number(row.subtotal ?? row.amount ?? 0),
    tax: Number(row.tax ?? row.taxAmount ?? 0),
    discount: Number(row.discount ?? row.discountAmount ?? 0),
    total: Number(row.total ?? row.finalAmount ?? 0),
    currency: String(row.currency ?? ""),
    lineItems,
    timeline: Array.isArray(row.timeline)
      ? row.timeline.map(mapTimelineEvent)
      : undefined,
    createdAt: row.createdAt || new Date().toISOString(),
    updatedAt: row.updatedAt ?? undefined,
  };
};

export const getTransactions = createAsyncThunk(
  "transactions/getTransactions",
  async (params: GetTransactionsParams, { rejectWithValue }) => {
    try {
      const response = await transactionService.getTransactions(params);
      const payload = response.data?.data as PaginatedListResponse<TransactionRecord>;
      const rows = payload?.data ?? [];

      return {
        params,
        data: rows.map(mapTransaction),
        total: payload?.totalRecord ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transactions"
      );
    }
  }
);

export const getTransaction = createAsyncThunk(
  "transactions/getTransaction",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await transactionService.getTransaction(id);
      const data = response.data?.data as TransactionRecord | undefined;

      return data?.id ? mapTransaction(data) : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transaction"
      );
    }
  }
);

const initialState: TransactionsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: SortOrder.DESC,
  },
  selectedTransaction: null,
  loading: false,
  detailLoading: false,
  error: null,
};

export const transactionsSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedTransaction: (state) => {
      state.selectedTransaction = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getTransaction.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getTransaction.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedTransaction = action.payload;
      })
      .addCase(getTransaction.rejected, (state) => {
        state.detailLoading = false;
        state.selectedTransaction = null;
      });
  },
});

export const { clearError, clearSelectedTransaction } =
  transactionsSlice.actions;

export default transactionsSlice.reducer;
